import React from 'react';

import CalendarAssignmentsHeader, {
  CalendarAssignmentsHeaderProps
} from './CalendarAssignmentsHeader';
import { CalendarAssignmentsTimeline } from './CalendarAssignmentsTimeline';
import { useTimelineLayoutMetrics } from './hooks/useTimelineLayoutMetrics';
import {
  CALENDAR_ASSIGNMENTS_DESKTOP_LAYOUT,
  CalendarAssignmentsDesktopLayout,
  CalendarAssignmentsTimelineActions,
  CalendarAssignmentsTimelineModel
} from './types';

const MOBILE_TIMELINE_SCROLL_BREAKPOINT = '(max-width: 768px)';
const HORIZONTAL_DRAG_THRESHOLD_PX = 6;
const VERTICAL_DRAG_THRESHOLD_PX = 6;

interface HorizontalDragState {
  pointerId: number;
  startX: number;
  startY: number;
  scrollLeft: number;
  isDragging: boolean;
  hasPointerCapture: boolean;
}
interface VerticalDragState {
  pointerId: number;
  startX: number;
  startY: number;
  scrollTop: number;
  isDragging: boolean;
  hasPointerCapture: boolean;
}

function isMobileTimelineViewport() {
  if (typeof window === 'undefined') return false;

  if (typeof window.matchMedia === 'function') {
    const mobileQuery = window.matchMedia(MOBILE_TIMELINE_SCROLL_BREAKPOINT);
    if (mobileQuery) return mobileQuery.matches;
  }

  return window.innerWidth <= 768;
}

function useMobileHorizontalDragScroll(
  scrollContainerRef: React.RefObject<HTMLDivElement>
) {
  const dragStateRef = React.useRef<HorizontalDragState | null>(null);
  const suppressClickRef = React.useRef(false);

  const resetSuppressedClick = React.useCallback(() => {
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
  }, []);

  const handlePointerDownCapture = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.isPrimary === false || event.button > 0) return;

      const container = scrollContainerRef.current;
      if (!container || !isMobileTimelineViewport()) return;
      if (container.scrollWidth <= container.clientWidth) return;

      dragStateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        scrollLeft: container.scrollLeft,
        isDragging: false,
        hasPointerCapture: false
      };
    },
    [scrollContainerRef]
  );

  const handlePointerMoveCapture = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const state = dragStateRef.current;
      const container = scrollContainerRef.current;
      if (!state || !container || state.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - state.startX;
      const deltaY = event.clientY - state.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (!state.isDragging) {
        if (absY > HORIZONTAL_DRAG_THRESHOLD_PX && absY > absX) {
          dragStateRef.current = null;
          return;
        }

        if (absX < HORIZONTAL_DRAG_THRESHOLD_PX || absX <= absY) return;

        state.isDragging = true;

        if (!state.hasPointerCapture) {
          try {
            event.currentTarget.setPointerCapture(event.pointerId);
            state.hasPointerCapture = true;
          } catch {
            state.hasPointerCapture = false;
          }
        }
      }

      event.preventDefault();
      event.stopPropagation();
      container.scrollLeft = state.scrollLeft - deltaX;
    },
    [scrollContainerRef]
  );

  const finishPointerGesture = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const state = dragStateRef.current;
      if (!state || state.pointerId !== event.pointerId) return;

      if (state.isDragging) {
        event.preventDefault();
        event.stopPropagation();
        suppressClickRef.current = true;
        resetSuppressedClick();
      }

      if (state.hasPointerCapture) {
        try {
          event.currentTarget.releasePointerCapture(event.pointerId);
        } catch {
          // The browser may have already released capture after pointerup.
        }
      }

      dragStateRef.current = null;
    },
    [resetSuppressedClick]
  );

  const handleClickCapture = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!suppressClickRef.current) return;

      event.preventDefault();
      event.stopPropagation();
      suppressClickRef.current = false;
    },
    []
  );

  return React.useMemo(
    () => ({
      onPointerDownCapture: handlePointerDownCapture,
      onPointerMoveCapture: handlePointerMoveCapture,
      onPointerUpCapture: finishPointerGesture,
      onPointerCancelCapture: finishPointerGesture,
      onClickCapture: handleClickCapture
    }),
    [
      finishPointerGesture,
      handleClickCapture,
      handlePointerDownCapture,
      handlePointerMoveCapture
    ]
  );
}

function useMobileVerticalDragScroll(
  scrollContainerRef: React.RefObject<HTMLDivElement>
) {
  const dragStateRef = React.useRef<VerticalDragState | null>(null);
  const suppressClickRef = React.useRef(false);

  const resetSuppressedClick = React.useCallback(() => {
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
  }, []);

  const handlePointerDownCapture = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.isPrimary === false || event.button > 0) return;

      const container = scrollContainerRef.current;
      if (!container || !isMobileTimelineViewport()) return;
      if (container.scrollHeight <= container.clientHeight) return;

      dragStateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        scrollTop: container.scrollTop,
        isDragging: false,
        hasPointerCapture: false
      };
    },
    [scrollContainerRef]
  );

  const handlePointerMoveCapture = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const state = dragStateRef.current;
      const container = scrollContainerRef.current;
      if (!state || !container || state.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - state.startX;
      const deltaY = event.clientY - state.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (!state.isDragging) {
        if (absX > VERTICAL_DRAG_THRESHOLD_PX && absX > absY) {
          dragStateRef.current = null;
          return;
        }

        if (absY < VERTICAL_DRAG_THRESHOLD_PX || absY <= absX) return;

        state.isDragging = true;

        if (!state.hasPointerCapture) {
          try {
            event.currentTarget.setPointerCapture(event.pointerId);
            state.hasPointerCapture = true;
          } catch {
            state.hasPointerCapture = false;
          }
        }
      }

      event.preventDefault();
      event.stopPropagation();
      container.scrollTop = state.scrollTop - deltaY;
    },
    [scrollContainerRef]
  );

  const finishPointerGesture = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const state = dragStateRef.current;
      if (!state || state.pointerId !== event.pointerId) return;

      if (state.isDragging) {
        event.preventDefault();
        event.stopPropagation();
        suppressClickRef.current = true;
        resetSuppressedClick();
      }

      if (state.hasPointerCapture) {
        try {
          event.currentTarget.releasePointerCapture(event.pointerId);
        } catch {
          // The browser may have already released capture after pointerup.
        }
      }

      dragStateRef.current = null;
    },
    [resetSuppressedClick]
  );

  const handleClickCapture = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!suppressClickRef.current) return;

      event.preventDefault();
      event.stopPropagation();
      suppressClickRef.current = false;
    },
    []
  );

  return React.useMemo(
    () => ({
      onPointerDownCapture: handlePointerDownCapture,
      onPointerMoveCapture: handlePointerMoveCapture,
      onPointerUpCapture: finishPointerGesture,
      onPointerCancelCapture: finishPointerGesture,
      onClickCapture: handleClickCapture
    }),
    [
      finishPointerGesture,
      handleClickCapture,
      handlePointerDownCapture,
      handlePointerMoveCapture
    ]
  );
}

export interface CalendarAssignmentsViewProps {
  isDepartmentSelected: boolean;
  headerProps: Omit<CalendarAssignmentsHeaderProps, 'hasHorizontalScroll'>;
  timelineModel: CalendarAssignmentsTimelineModel;
  timelineActions: CalendarAssignmentsTimelineActions;
  layout?: CalendarAssignmentsDesktopLayout;
}

export function CalendarAssignmentsView({
  isDepartmentSelected,
  headerProps,
  timelineModel,
  timelineActions,
  layout = CALENDAR_ASSIGNMENTS_DESKTOP_LAYOUT
}: CalendarAssignmentsViewProps) {
  const {
    calendarAssignmentTableRef,
    contentWrapperRef,
    employeesTimelineRef,
    hasHorizontalScroll,
    scrollBarOffset,
    scrollHeight,
    checkScrollbar
  } = useTimelineLayoutMetrics({
    timelineScale: timelineModel.timelineScale,
    groups: timelineModel.groups,
    items: timelineModel.items
  });
  const mobileHorizontalDragHandlers =
    useMobileHorizontalDragScroll(contentWrapperRef);
  const mobileVerticalDragHandlers =
    useMobileVerticalDragScroll(employeesTimelineRef);

  return (
    <div
      ref={calendarAssignmentTableRef}
      className="calendarAssignment-table wrapper wrapper--main"
    >
      <div className="p-datatable p-datatable-sm">
        <div className="p-datatable-header calendarAssignments--header">
          <CalendarAssignmentsHeader
            {...headerProps}
            hasHorizontalScroll={hasHorizontalScroll}
          />
        </div>
      </div>
      <div
        ref={contentWrapperRef}
        className="timeline-head calendarAssignments--content-wrapper"
        {...mobileHorizontalDragHandlers}
      >
        {!isDepartmentSelected ? (
          <div className="calendarAssignments--empty-state">
            Выберите участок для отображения информации
          </div>
        ) : (
          <CalendarAssignmentsTimeline
            model={timelineModel}
            actions={timelineActions}
            layout={layout}
            employeesTimelineRef={employeesTimelineRef}
            employeesTimelinePointerHandlers={mobileVerticalDragHandlers}
            scrollBarOffset={scrollBarOffset}
            scrollHeight={scrollHeight}
            onEmployeesScroll={checkScrollbar}
          />
        )}
      </div>
    </div>
  );
}
